import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { LOTUS_PROVIDERS_PATH } from '../../constants/mainCasinoRoute'
import { useCasinoUi } from '../../context/CasinoUiContext'
import {
  filterProviderGames,
  formatProviderDisplayName,
  useLotusProviderPageData,
  type LotusProviderCategory,
} from '../../hooks/useLotusProviderPageData'
import { getGameId } from '../../utils/lotusCasinoGameFilter'
import LotusCasinoGameCard from '../LotusCasinoGameCard/LotusCasinoGameCard'
import '../../apollo-icons.css'
import '../LotusCasinoLobby/LotusCasinoLobby.css'
import './LotusCasinoProviderGames.css'

export type LotusCasinoProviderGamesProps = {
  preLoginMode?: boolean
  embeddedCategory?: string | null
  onBack?: () => void
}

const LotusCasinoProviderGames: React.FC<LotusCasinoProviderGamesProps> = ({
  preLoginMode = false,
  embeddedCategory,
  onBack,
}) => {
  const navigate = useNavigate()
  const routeParams = useParams()
  const [searchParams] = useSearchParams()
  const { playGame } = useCasinoUi()

  const category =
    embeddedCategory ?? searchParams.get('category') ?? null
  const casinoType = routeParams.type ?? 'slots'
  const [catName, setCatName] = useState('All')

  useEffect(() => {
    setCatName('All')
  }, [category])

  const { games, categorylist, loading, visibilityConfig } = useLotusProviderPageData(
    category,
    casinoType,
    { preLoginMode },
  )

  const visibleGames = useMemo(
    () => filterProviderGames(games, 'All', visibilityConfig),
    [games, visibilityConfig],
  )

  const filteredGames = useMemo(
    () => filterProviderGames(games, catName, visibilityConfig),
    [games, catName, visibilityConfig],
  )

  const sectionTitle =
    catName === 'All'
      ? 'All Games'
      : `${catName} Games`

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    navigate(LOTUS_PROVIDERS_PATH)
  }

  if (!category) return null

  const providerLabel = formatProviderDisplayName(category)

  return (
    <div className="lotus-casino-provider-games">
      <button type="button" className="lotus-casino-provider-games__back" onClick={handleBack}>
        <i className="icon icon-angle-left" aria-hidden />
        <span>Back</span>
      </button>

      <div className="lotus-casino-provider-games__header">
        <div className="lotus-casino-provider-games__name">{providerLabel}</div>
        <div className="lotus-casino-provider-games__counter">
          Games: {loading ? '…' : visibleGames.length}
        </div>
      </div>

      {categorylist.length > 0 && (
        <div className="lotus-casino-provider-games__subcats">
          <button
            type="button"
            className={`lotus-casino-provider-games__subcat${catName === 'All' ? ' is-active' : ''}`}
            onClick={() => setCatName('All')}
          >
            All
          </button>
          {categorylist.map((cat: LotusProviderCategory) => (
            <button
              key={cat._id}
              type="button"
              className={`lotus-casino-provider-games__subcat${catName === cat._id ? ' is-active' : ''}`}
              onClick={() => setCatName(cat._id)}
            >
              {cat._id}
            </button>
          ))}
        </div>
      )}

      <div className="lotus-casino-provider-games__section-title">{sectionTitle}</div>

      <div className="lotus-casino-lobby-group__grid lotus-casino-provider-games__grid">
        {loading &&
          filteredGames.length === 0 &&
          Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="lotus-casino-lobby-skeleton" />
          ))}
        {filteredGames.map((game, index) => (
          <LotusCasinoGameCard
            key={getGameId(game) || index}
            game={game}
            onPlay={playGame}
            compact
            overrideImages={visibilityConfig.overrideImages}
            useCustomImage={visibilityConfig.useCustomImage}
            showFavorite={!preLoginMode}
          />
        ))}
        {!loading && filteredGames.length === 0 && (
          <div className="lotus-casino-provider-games__empty">No games found in this category.</div>
        )}
      </div>
    </div>
  )
}

export default LotusCasinoProviderGames
